import { useState } from 'react';
import { Card, CardContent, CardHeader } from '../../../components/Card';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import toast from 'react-hot-toast';

const CAMPAIGNS: Array<{
  id: string; name: string; type: string; status: string; scheduledAt: string;
  audience: number; segment: string;
  metrics: Record<string, number>;
  subject: string; tags: string[];
  createdBy: string; createdAt: string;
}> = [];

const SEGMENTS: Array<{
  id: string; name: string; count: number; description: string; filters: string[];
}> = [];

const CALENDAR_EVENTS: Array<{
  date: number; type: string; label: string;
}> = [];

const typeIcon: Record<string, string> = { email: '', social: '', 'multi-channel': '' };
const statusVariant: Record<string, 'success' | 'primary' | 'warning' | 'default' | 'error'> = {
  active: 'success', scheduled: 'primary', draft: 'default', paused: 'warning', completed: 'default',
};
const calTypeColor: Record<string, string> = { email: 'bg-primary/20 text-primary', social: 'bg-success/20 text-success', 'multi-channel': 'bg-primary/20 text-primary' };

export function Campaigns() {
  const [activeTab, setActiveTab] = useState<'campaigns' | 'calendar' | 'segments' | 'analytics'>('campaigns');
  const [typeFilter, setTypeFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedCampaign, setSelectedCampaign] = useState<typeof CAMPAIGNS[0] | null>(null);

  const filtered = CAMPAIGNS.filter(c => {
    const matchType = typeFilter === 'All' || c.type === typeFilter;
    const matchStatus = statusFilter === 'All' || c.status === statusFilter;
    return matchType && matchStatus;
  });

  if (selectedCampaign) {
    const m = selectedCampaign.metrics as any;
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Button variant="unstyled" size="unstyled" onClick={() => setSelectedCampaign(null)} className="hover:text-primary transition-colors font-semibold">Campaigns</Button>
          <span>/</span>
          <span className="text-foreground font-semibold">{selectedCampaign.name}</span>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <span className="text-xl">{typeIcon[selectedCampaign.type]}</span>
                  <Badge variant={statusVariant[selectedCampaign.status]}>{selectedCampaign.status}</Badge>
                  <span className="text-xs text-muted-foreground">{selectedCampaign.scheduledAt}</span>
                </div>
                <h1 className="tracking-tight text-foreground font-display text-page-title">{selectedCampaign.name}</h1>
                {selectedCampaign.subject && <p className="text-sm text-muted-foreground mt-1">Subject: {selectedCampaign.subject}</p>}
                <p className="text-xs text-muted-foreground mt-1">Segment: {selectedCampaign.segment} · {selectedCampaign.audience.toLocaleString()} recipients · Created by {selectedCampaign.createdBy}</p>
              </div>
              <div className="flex gap-2">
                {selectedCampaign.status === 'draft' && <Button variant="primary" size="sm" onClick={() => toast.success('Campaign scheduled!')}>Schedule</Button>}
                {selectedCampaign.status === 'paused' && <Button variant="primary" size="sm" onClick={() => toast.success('Campaign resumed!')}>▶ Resume</Button>}
                {selectedCampaign.status === 'active' && <Button variant="warning" size="sm" onClick={() => toast.success('Campaign paused.')}>Pause</Button>}
                <Button variant="secondary" size="sm" onClick={() => toast.success('Editing campaign...')}>Edit</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {selectedCampaign.status === 'completed' || selectedCampaign.status === 'active' || selectedCampaign.status === 'paused' ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {(selectedCampaign.type === 'email' ? [
                { label: 'Sent', value: m.sent?.toLocaleString() || '—', color: 'text-foreground', icon: '' },
                { label: 'Open Rate', value: m.openRate ? `${m.openRate}%` : '—', color: 'text-success', icon: '' },
                { label: 'Click Rate', value: m.clickRate ? `${m.clickRate}%` : '—', color: 'text-primary', icon: '' },
                { label: 'Unsubscribed', value: m.unsubscribed?.toString() || '0', color: 'text-destructive', icon: '' },
              ] : [
                { label: 'Impressions', value: m.impressions?.toLocaleString() || '—', color: 'text-foreground', icon: '' },
                { label: 'Engagements', value: m.engagements?.toLocaleString() || '—', color: 'text-success', icon: '' },
                { label: 'Shares', value: m.shares?.toString() || '—', color: 'text-primary', icon: '' },
                { label: 'CTR', value: m.clickRate ? `${m.clickRate}%` : '—', color: 'text-warning', icon: '' },
              ]).map((s, i) => (
                <Card key={i}><CardContent className="p-5">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs text-muted-foreground font-semibold mb-1">{s.label}</p>
                      <p className={`text-3xl font-semibold ${s.color}`}>{s.value}</p>
                    </div>
                    <span className="text-2xl opacity-40">{s.icon}</span>
                  </div>
                </CardContent></Card>
              ))}

            </div>

            {selectedCampaign.type === 'email' && m.sent > 0 && (
              <Card>
                <CardHeader className="bg-card/30 px-6 py-4"><h2 className="text-foreground font-display text-section-title">Email Funnel</h2></CardHeader>
                <CardContent className="p-6 space-y-3">
                  {[
                    { label: 'Sent', count: m.sent, pct: 100, color: 'bg-text-600' },
                    { label: 'Delivered', count: m.delivered, pct: ((m.delivered / m.sent) * 100).toFixed(1), color: 'bg-primary' },
                    { label: 'Opened', count: m.opened, pct: m.openRate, color: 'bg-success' },
                    { label: 'Clicked', count: m.clicked, pct: ((m.clicked / m.opened) * 100).toFixed(1), color: 'bg-warning' },
                    { label: 'Bounced', count: m.bounced, pct: ((m.bounced / m.sent) * 100).toFixed(1), color: 'bg-destructive' },
                  ].map((s, i) => (
                    <div key={i}>
                      <div className="flex justify-between text-sm font-semibold mb-1.5">
                        <span className="text-muted-foreground">{s.label}</span>
                        <span className="text-foreground">{s.count.toLocaleString()} ({s.pct}%)</span>
                      </div>
                      <div className="h-2.5 bg-background rounded-full overflow-hidden">
                        <div className={`h-full ${s.color} rounded-full transition-all duration-700`} style={{ width: `${s.pct}%` }} />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          <Card><CardContent className="p-8 text-center">
            <span className="text-4xl"></span>
            <p className="font-semibold text-muted-foreground mt-3">Analytics will appear once the campaign is active</p>
          </CardContent></Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="tracking-tight text-foreground font-display text-page-title">Marketing <span className="text-foreground">Hub</span></h1>
          <p className="text-muted-foreground mt-1">Campaigns · Content Calendar · Audience Segments · Analytics</p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => setActiveTab('calendar')}>Calendar</Button>
          <Button variant="primary" onClick={() => toast.success('New campaign wizard...')}>+ New Campaign</Button>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Active Campaigns', value: CAMPAIGNS.filter(c => c.status === 'active').length, color: 'text-success' },
          { label: 'Total Audience', value: '32.4k', color: 'text-primary' },
          { label: 'Avg Open Rate', value: '35%', color: 'text-warning' },
          { label: 'Avg Click Rate', value: '21.4%', color: 'text-info' },
        ].map((s, i) => (
          <Card key={i}><CardContent className="p-5">
            <p className="text-xs text-muted-foreground font-semibold mb-1">{s.label}</p>
            <p className={`text-3xl font-semibold ${s.color}`}>{s.value}</p>
          </CardContent></Card>
        ))}
      </div>

      <div className="flex gap-2 border-b border-border">
        {[
          { id: 'campaigns', label: 'Campaigns' },
          { id: 'calendar', label: 'Content Calendar' },
          { id: 'segments', label: 'Audience Segments' },
          { id: 'analytics', label: 'Analytics' },
        ].map(t => (
          <Button variant="unstyled" size="unstyled" key={t.id} onClick={() => setActiveTab(t.id as any)}
            className={`pb-4 px-4 text-sm font-semibold transition-all relative ${activeTab === t.id ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
            {t.label}
            {activeTab === t.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />}
          </Button>
        ))}
      </div>

      {activeTab === 'campaigns' && (
        <div className="space-y-4">
          <div className="flex gap-3 flex-wrap">
            {['All', 'email', 'social', 'multi-channel'].map(f => (
              <Button variant="unstyled" size="unstyled" key={f} onClick={() => setTypeFilter(f)}
                className={`px-3 py-2 text-xs font-semibold rounded-lg border capitalize transition-all ${typeFilter === f ? 'bg-primary text-white border-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}>
                {f === 'All' ? 'All Types' : `${typeIcon[f]} ${f}`}
              </Button>
            ))}
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="ml-auto bg-background border border-border rounded-lg px-3 py-2 text-foreground text-sm focus:outline-none">
              {['All', 'draft', 'scheduled', 'active', 'paused', 'completed'].map(s => <option key={s}>{s === 'All' ? 'All Status' : s}</option>)}
            </select>
          </div>

          <div className="space-y-3">
            {filtered.map(c => (
              <Card key={c.id} className="hover:-translate-y-0.5 transition-all cursor-pointer group" onClick={() => setSelectedCampaign(c)}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-xl shrink-0">{typeIcon[c.type]}</div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1 flex-wrap">
                          <p className="font-semibold text-foreground group-hover:text-primary transition-colors">{c.name}</p>
                          <Badge variant={statusVariant[c.status]}>{c.status}</Badge>
                        </div>
                        {c.subject && <p className="text-xs text-muted-foreground mb-1">"{c.subject}"</p>}
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>{c.scheduledAt}</span>
                          <span>{c.audience.toLocaleString()} audience</span>
                          <span>By {c.createdBy} · {c.createdAt}</span>
                          {c.tags.map(t => <span key={t} className="px-1.5 py-0.5 bg-primary/10 text-primary rounded-md font-semibold">{t}</span>)}
                        </div>
                      </div>
                    </div>
                    <div className="text-right text-xs">
                      {(c.metrics as any).openRate > 0 && (
                        <div>
                          <p className="text-success font-semibold">{(c.metrics as any).openRate}%</p>
                          <p className="text-muted-foreground">open rate</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'calendar' && (
        <Card>
          <CardHeader className="bg-card/30 px-6 py-4 flex flex-row justify-between items-center">
            <h2 className="text-foreground font-display text-section-title">Content Calendar — March 2026</h2>
            <div className="flex gap-2 text-xs">
              <span className="px-2 py-1 bg-primary/20 text-primary rounded-lg">Email</span>
              <span className="px-2 py-1 bg-success/20 text-success rounded-lg">Social</span>
              <span className="px-2 py-1 bg-primary/20 text-primary rounded-lg">Multi-channel</span>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-7 gap-1 mb-1">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                <div key={d} className="text-center text-xs font-semibold text-muted-foreground py-2">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {/* Mar 2026 starts on Sunday=0 */}
              {Array.from({ length: 31 }, (_, d) => d + 1).map(day => {
                const events = CALENDAR_EVENTS.filter(e => e.date === day);
                const isToday = day === 9;
                return (
                  <div key={day} className={`min-h-[90px] p-1.5 rounded-lg border transition-colors cursor-pointer ${isToday ? 'border-primary/50 bg-primary/5' : 'border-border hover:border-primary/20'}`}
                    onClick={() => (events.length > 0 || isToday) && toast.success(`Mar ${day}: ${events.length > 0 ? events.map(e => e.label).join(', ') : 'Today'}`)}>
                    <p className={`text-sm font-semibold mb-1 ${isToday ? 'text-primary bg-primary/20 w-6 h-6 rounded-full flex items-center justify-center' : 'text-muted-foreground'}`}>{day}</p>
                    <div className="space-y-0.5">
                      {events.map((e, i) => (
                        <div key={i} className={`text-xs font-semibold px-1.5 py-0.5 rounded truncate ${calTypeColor[e.type]}`}>
                          {typeIcon[e.type]} {e.label}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'segments' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SEGMENTS.map(seg => (
            <Card key={seg.id} className="hover:-translate-y-1 transition-all cursor-pointer group" onClick={() => toast.success(`Segment: ${seg.name}`)}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-semibold text-foreground group-hover:text-primary transition-colors">{seg.name}</p>
                  <p className="text-primary font-display text-metric">{seg.count.toLocaleString()}</p>
                </div>
                <p className="text-xs text-muted-foreground mb-3">{seg.description}</p>
                <div className="space-y-1">
                  {seg.filters.map(f => (
                    <div key={f} className="text-xs font-mono px-2 py-1 bg-background border border-border rounded-lg text-muted-foreground">{f}</div>
                  ))}
                </div>
                <div className="flex gap-2 mt-4">
                  <Button variant="secondary" size="sm" onClick={(e: React.MouseEvent) => { e.stopPropagation(); toast.success(`Using segment: ${seg.name}`); }}>Use in Campaign</Button>

                  <Button variant="secondary" size="sm" onClick={(e: React.MouseEvent) => { e.stopPropagation(); toast.success('Editing segment...'); }}>Edit</Button>

                </div>
              </CardContent>
            </Card>
          ))}
          <Card className="border-dashed hover:-translate-y-1 transition-all cursor-pointer" onClick={() => toast.success('Create audience segment...')}>
            <CardContent className="p-5 flex flex-col items-center justify-center gap-3 min-h-[180px]">
              <div className="w-12 h-12 rounded-lg border-2 border-dashed border-border flex items-center justify-center text-2xl text-muted-foreground">+</div>
              <p className="font-semibold text-muted-foreground">New Segment</p>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="space-y-6">
          <Card>
            <CardHeader className="bg-card/30 px-6 py-4"><h2 className="text-foreground font-display text-section-title">Campaign Performance Overview — March 2026</h2></CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                {CAMPAIGNS.filter(c => (c.metrics as any).sent > 0).map(c => {
                  const m = c.metrics as any;
                  return (
                    <div key={c.id} className="flex items-center gap-4">
                      <span className="text-xl shrink-0">{typeIcon[c.type]}</span>
                      <div className="flex-1">
                        <div className="flex justify-between text-sm font-semibold mb-1">
                          <span className="text-foreground">{c.name}</span>
                          <span className={m.openRate >= 30 ? 'text-success' : m.openRate >= 20 ? 'text-warning' : 'text-destructive'}>{m.openRate}% open</span>
                        </div>
                        <div className="h-2 bg-background rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${m.openRate >= 30 ? 'bg-success' : m.openRate >= 20 ? 'bg-warning' : 'bg-destructive'}`} style={{ width: `${m.openRate}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
